import { faClone } from '@fortawesome/free-regular-svg-icons';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';
import {
  Button, Card, CardBody, CardFooter, CardHeader, Form, Modal, ModalBody, ModalFooter, ModalHeader,
} from 'reactstrap';

import Loading from 'src/components/Loading';
import { ClauseEdit, ClauseShow } from 'src/components/TradeRule/TimeOfUse';
import FlashesStore from 'src/stores/FlashesStore';
import {
  TRADE_DIRECTION_BUY, TRADE_DIRECTION_SELL,
  TRADE_TYPE_COMMUNITY, TRADE_TYPE_RESIDUAL,
} from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import { isCommunityPriceValid } from 'src/util/tradeRule/validation';

import {
  getClauses, validateClauses,
} from './helpers';
import { getPriceHelpText, getPriceLabel } from './i18n/helpers';

const TimeOfUsePricingFormPropertyFragment = graphql`
  fragment TimeOfUsePricingForm_property on Property @throwOnFieldError {
    uuid
    timezone
    publicHolidayRegion
  }
`;

const TimeOfUsePricingFormFormMeterFragment = graphql`
  fragment TimeOfUsePricingForm_meter on Meter @throwOnFieldError {
    uuid
    identifier
    title
    tradePointId
    communityRules: rules(first: 500, type: TRADE_TYPE_COMMUNITY, state: TRADE_RULE_STATE_ACCEPTED) {
      edges {
        node {
          uuid
          priority
          tradeType
          state
          buyer {
            userId
            communityId
            residualId
            tradePoint {
              uuid
            }
          }
          seller {
            userId
            communityId
            residualId
            tradePoint {
              uuid
            }
          }
          clauses {
            edges {
              node {
                price
                ignorePublicHolidays
                monthsOfYear
                daysOfWeek
                timesOfDay {
                  start  { hours minutes seconds }
                  finish { hours minutes seconds }
                }
              }
            }
          }
        }
      }
    }
    residualRules: rules(first: 500, type: TRADE_TYPE_RESIDUAL, state: TRADE_RULE_STATE_ACCEPTED, start: $now, finish: $now) {
      edges {
        node {
          uuid
          priority
          tradeType
          state
          buyer {
            userId
            communityId
            residualId
            tradePoint {
              uuid
            }
          }
          seller {
            userId
            communityId
            residualId
            tradePoint {
              uuid
            }
          }
          clauses {
            edges {
              node {
                price
                ignorePublicHolidays
                monthsOfYear
                daysOfWeek
                timesOfDay {
                  start  { hours minutes seconds }
                  finish { hours minutes seconds }
                }
              }
            }
          }
        }
      }
    }
  }
`;

/**
 * @param {object} props
 * @param {object} props.property Property data containing timezone and public holiday
 * region
 * @param {object} props.meter Meter data containing trade point ID and community rules
 * @param {object} props.router
 * @param {Function} props.onPricingButtonClick function handling the change of
 * state between the time of use pricing and flat pricing forms.
 * @param {Function} props.handleTradeRuleProposeCommunity  Mutation handler for
 * ProposeCommunityTradeMutation
 * @param {boolean} props.processing whether the mutation is being processed.
 * @returns {React.ReactComponentElement} - TimeOfUsePricingForm component
 */
function TimeOfUsePricingForm(props) {
  let { property, meter } = props;

  if (!property || !meter) {
    return <Loading />;
  }

  const {
    onPricingButtonClick, router, processing, handleTradeRuleProposeCommunity,
  } = props;
  const intl = useIntl();
  const intlCurrency = useIntlCurrency();
  property = useFragment(TimeOfUsePricingFormPropertyFragment, property);
  meter = useFragment(TimeOfUsePricingFormFormMeterFragment, meter);

  let clauseIdCounter = 0;

  /**
   * Generates a unique clause ID by incrementing the `clauseIdCounter` variable
   * and returning it as a string. This function is used to assign unique IDs
   * to new clauses added to the `clauses` state.
   * @returns {string} A unique clause ID.
   */
  const buildNextClauseId = () => {
    clauseIdCounter += 1;
    return clauseIdCounter.toString();
  };

  /**
   * Transforms GraphQL edge data into clause format for form processing.
   * @param {object} edge - GraphQL ClauseEdge containing clause node data
   * @param {object} edge.node - Clause node from GraphQL schema
   * @param {number} edge.node.price - Price value for the clause
   * @param {Array<string>} edge.node.monthsOfYear - Array of Month enum values when clause applies
   * @param {Array<string>} edge.node.daysOfWeek - Array of DayOfWeek enum values when clause
   * applies
   * @param {Array<object>} edge.node.timesOfDay - Array of TimeSpan objects when clause applies
   * @returns {object} Formatted clause object containing price, monthsOfYear, daysOfWeek,
   * timesOfDay,and generated id
   */
  const buildClauseFromEdge = (edge) => {
    const { node } = edge;
    const {
      price, monthsOfYear, daysOfWeek, timesOfDay,
    } = node;

    return {
      price,
      monthsOfYear,
      daysOfWeek,
      timesOfDay,
      id: buildNextClauseId(),
    };
  };

  const { communityRules } = meter || {};
  const initialBuyClauses = getClauses(
    meter,
    TRADE_DIRECTION_BUY,
    buildClauseFromEdge,
    TRADE_TYPE_COMMUNITY,
  );
  const initialSellClauses = getClauses(
    meter,
    TRADE_DIRECTION_SELL,
    buildClauseFromEdge,
    TRADE_TYPE_COMMUNITY,
  );

  const initialIgnorePublicHolidays = communityRules?.edges?.
    [0]?.node?.clauses?.edges?.[0]?.node?.ignorePublicHolidays
  || false;

  const [buyClauses, setBuyClauses] = useState(initialBuyClauses);
  const [sellClauses, setSellClauses] = useState(initialSellClauses);

  const [ignorePublicHolidays, setIgnorePublicHolidays] = useState(initialIgnorePublicHolidays);
  const [editing, setEditing] = useState(null); // { direction, index }
  const [showConfirmCoverage, setShowConfirmCoverage] = useState(false);

  /**
   * Sets the direction to be edited.
   * Sets the `editing` state to `null`, indicating that the component is no longer in editing mode.
   * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} direction
   */
  const addClause = (direction) => {
    setEditing({ direction, index: null });
  };

  /**
   * Removes the clause at the specified index from the `clauses` state.
   * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} tradeDirection
   * @param {number} index - The index of the clause to be removed.
   */
  const removeClause = (tradeDirection, index) => {
    if (tradeDirection === TRADE_DIRECTION_BUY) {
      const newBuyClauses = [...buyClauses];
      newBuyClauses.splice(index, 1);
      setBuyClauses(newBuyClauses);
    } else {
      const newSellClauses = [...sellClauses];
      newSellClauses.splice(index, 1);
      setSellClauses(newSellClauses);
    }
  };

  /**
   * Given a trade direction, it updates the `buy clauses` or `sell clauses` state by
   * replacing the clause at the specified index with the provided `newClause`. The new
   * clause is assigned the `id` of th existing clause at the same index. After the update,
   * the `editing` state is set to `null` to indicate that the component is no longer
   * in editing mode.
   * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} tradeDirection
   * @param {number} index - The index of the clause to be updated.
   * @param {object} newClause - The new clause object to be used for the update.
   */
  const editClauseSet = (tradeDirection, index, newClause) => {
    if (tradeDirection === TRADE_DIRECTION_BUY) {
      const newBuyClauses = [...buyClauses];
      newBuyClauses[index] = { ...newClause, id: buyClauses[index].id };
      setBuyClauses(newBuyClauses);
    } else {
      const newSellClauses = [...sellClauses];
      newSellClauses[index] = { ...newClause, id: sellClauses[index].id };
      setSellClauses(newSellClauses);
    }
    setEditing(null);
  };

  const addClauseSet = (tradeDirection, newClause) => {
    if (tradeDirection === TRADE_DIRECTION_BUY) {
      const newBuyClauses = [...buyClauses];
      newBuyClauses.push({ ...newClause, id: buildNextClauseId() });
      setBuyClauses(newBuyClauses);
    } else {
      const newSellClauses = [...sellClauses];
      newSellClauses.push({ ...newClause, id: buildNextClauseId() });
      setSellClauses(newSellClauses);
    }
    setEditing(null);
  };

  /**
   * Checks if the component is in editing mode for the given clause index.
   * If no index is provided, it checks if the component is in any editing mode.
   * @param {TRADE_DIRECTION_BUY | TRADE_DIRECTION_SELL} [tradeDirection]
   * @param {number|undefined} [index] - The index of the clause being edited, or
   * undefined to check for any editing mode.
   * @returns {boolean} - True if the component is in editing mode for the given
   * clause index, or any editing mode if no index is provided.
   */
  const isEditing = (tradeDirection, index) => {
    if (!editing) return false;
    if (tradeDirection !== undefined && index !== undefined) {
      return editing.direction === tradeDirection && editing.index === index;
    }
    return true;
  };

  /**
   * Extracts residual trade rule clauses from meter data for copying to community trade rules.
   * This function retrieves the first accepted residual trade rule from the meter and formats
   * its clauses for both buy and sell directions, along with the public holiday settings.
   * @returns {{buyClauses: Array<object>, sellClauses: Array<object>,
   *   ignorePublicHolidays: boolean}|null}
   * Returns object with formatted clause arrays and public holiday setting, or null if no residual
   * rules/clauses exist.
   * @example
   * // Returns formatted residual clauses for copying
   * const residualData = copyFromResidual();
   * // Returns: {
   * //   buyClauses: [{
   * //     price: 0.25,
   * //     monthsOfYear: ['JANUARY', 'FEBRUARY'],
   * //     daysOfWeek: ['MONDAY', 'TUESDAY'],
   * //     timesOfDay: [{ start: {hours: 9, minutes: 0, seconds: 0},
   * //                   finish: {hours: 17, minutes: 0, seconds: 0} }],
   * //     id: 'generated-uuid'
   * //   }],
   * //   sellClauses: [...],
   * //   ignorePublicHolidays: false
   * // }
   * @example
   * // Returns null when no residual rules exist
   * const emptyMeter = { residualRules: { edges: [] } };
   * const result = copyFromResidual(); // Returns: null
   */
  const copyFromResidual = () => {
    if (!meter || !meter.residualRules || meter.residualRules.edges.length === 0) {
      return null;
    }

    const residualRule = meter.residualRules.edges[0].node;
    if (!residualRule.clauses || residualRule.clauses.edges.length === 0) {
      return null;
    }

    const residualBuyClauses = getClauses(
      meter,
      TRADE_DIRECTION_BUY,
      buildClauseFromEdge,
      TRADE_TYPE_RESIDUAL,
    );
    const residualSellClauses = getClauses(
      meter,
      TRADE_DIRECTION_SELL,
      buildClauseFromEdge,
      TRADE_TYPE_RESIDUAL,
    );

    return {
      buyClauses: residualBuyClauses,
      sellClauses: residualSellClauses,
      ignorePublicHolidays: residualRule.clauses.edges[0].node.ignorePublicHolidays || false,
    };
  };

  const mutationName = 'proposeCommunityTrade';
  const {
    valid: validBuyClauses,
    coverage: buyCoverage,
  } = validateClauses(buyClauses, ignorePublicHolidays);
  const {
    valid: validSellClauses,
    coverage: sellCoverage,
  } = validateClauses(sellClauses, ignorePublicHolidays);
  const coverage = buyCoverage && sellCoverage;
  const areBuyAndSellClausesValid = validBuyClauses && validSellClauses;

  const handleSubmit = (event) => {
    event.preventDefault();

    const { timezone, publicHolidayRegion } = property;
    const { tradePointId } = meter;

    const processingMsg = intl.formatMessage({
      id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.processing.message',
      defaultMessage: 'We are still processing your request...',
    });

    const errorMsg = intl.formatMessage({
      id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.validation_error.message',
      defaultMessage: 'Form data not valid. Please see below.',
    });

    if (processing) {
      FlashesStore.flash(FlashesStore.INFO, processingMsg, mutationName);
      return;
    }

    if (!areBuyAndSellClausesValid) {
      FlashesStore.flash(FlashesStore.ERROR, errorMsg, mutationName);
      return;
    }

    if (!showConfirmCoverage && !coverage) {
      setShowConfirmCoverage(!showConfirmCoverage);
      return;
    }

    const formatClause = (clause) => {
      const {
        price, monthsOfYear, daysOfWeek, timesOfDay,
      } = clause;

      return {
        price,
        timezone,
        publicHolidayRegion,
        ignoreDaylightSavings: false,
        ignorePublicHolidays,
        monthsOfYear,
        daysOfWeek,
        timesOfDay,
      };
    };

    const input = {
      tradePointId,
      buyClauses: buyClauses.map(formatClause),
      sellClauses: sellClauses.map(formatClause),
    };

    handleTradeRuleProposeCommunity(input);
  };

  const showClause = (clause, index, tradeDirection) => {
    if (isEditing(tradeDirection, index)) {
      return (
        <ClauseEdit
          clause={clause}
          setClause={
            (newClause) => editClauseSet(tradeDirection, index, newClause)
          }
          cancelEdit={() => setEditing(null)}
          priceLabel={getPriceLabel(intl, tradeDirection)}
          priceHelpText={getPriceHelpText(intl, intlCurrency, tradeDirection)}
          priceValidation={isCommunityPriceValid(intlCurrency, tradeDirection)}
          key={clause.id}
        />
      );
    }
    return (
      <ClauseShow
        clause={clause}
        showControls={!isEditing()}
        editClause={() => setEditing({ direction: tradeDirection, index })}
        removeClause={() => removeClause(tradeDirection, index)}
        key={clause.id}
      />
    );
  };

  const pageTitle = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.page.title',
    defaultMessage: 'Set community trade rules for {meterTitle}',
  }, { meterTitle: meter.title });

  const flatPricingLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.flat_pricing.button.label',
    defaultMessage: 'Flat pricing',
  });

  const buyHeaderLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.buy_section.header.label',
    defaultMessage: 'Buy',
  });

  const addClauseLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.add_clause.button.label',
    defaultMessage: 'Add clause',
  });

  const sellHeaderLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.sell_section.header.label',
    defaultMessage: 'Sell',
  });

  const setLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.submit.button.label',
    defaultMessage: 'Set',
  });

  const cancelLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.cancel.button.label',
    defaultMessage: 'Cancel',
  });

  const copyFromResidualLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.copy_from_residual.button.label',
    defaultMessage: 'Copy from retailer default',
  });

  const noFullCoverageLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.no_full_coverage.modal.title',
    defaultMessage: 'No full time coverage',
  });

  const noFullCoverageHelpText = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.no_full_coverage.modal.message',
    defaultMessage: 'Clauses of proposed community trade rule do not cover all possible times. Confirm that this is okay.',
  });

  const confirmLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.modal.confirm.button.label',
    defaultMessage: 'Confirm',
  });

  const continueEditingLabel = intl.formatMessage({
    id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.modal.continue_editing.button.label',
    defaultMessage: 'Continue editing',
  });

  useEffect(() => {
    FlashesStore.reset();
    if (!areBuyAndSellClausesValid) {
      const clausesOverlappingErrorMsg = intl.formatMessage({ id: 'trade_rule.trade_rule_set_community.time_of_use_pricing_form.invalid_clauses.response_message.error', defaultMessage: 'Form data not valid. At least one buy clause and one sell clause are required. Clauses can not have overlapping times.' });
      FlashesStore.flash(FlashesStore.ERROR, clausesOverlappingErrorMsg, mutationName);
    }
  }, [buyClauses, sellClauses]);

  return (
    <Form onSubmit={handleSubmit}>
      <Card>
        <CardHeader className="d-flex flex-wrap">
          <h2 className="mb-0">
            {pageTitle}
          </h2>
          <Button color="darken" className="ms-auto" onClick={onPricingButtonClick}>
            {flatPricingLabel}
          </Button>
        </CardHeader>
        <CardBody>
          <div>
            <h3>{buyHeaderLabel}</h3>
            {buyClauses.map((clause, index) => (
              showClause(clause, index, TRADE_DIRECTION_BUY)
            ))}
            {isEditing(TRADE_DIRECTION_BUY, null) && (
              <ClauseEdit
                setClause={(newClause) => addClauseSet(TRADE_DIRECTION_BUY, newClause)}
                cancelEdit={() => setEditing(null)}
                priceLabel={getPriceLabel(intl, TRADE_DIRECTION_BUY)}
                priceHelpText={getPriceHelpText(intl, intlCurrency, TRADE_DIRECTION_BUY)}
                priceValidation={isCommunityPriceValid(intlCurrency, TRADE_DIRECTION_BUY)}
              />
            )}
            {!isEditing() && (
              <Button color="darken" onClick={() => addClause(TRADE_DIRECTION_BUY)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {addClauseLabel}
              </Button>
            )}
          </div>
          <div className="mt-4">
            <h3>{sellHeaderLabel}</h3>
            {sellClauses.map((clause, index) => (
              showClause(clause, index, TRADE_DIRECTION_SELL)
            ))}
            {isEditing(TRADE_DIRECTION_SELL, null) && (
              <ClauseEdit
                setClause={(newClause) => addClauseSet(TRADE_DIRECTION_SELL, newClause)}
                cancelEdit={() => setEditing(null)}
                priceLabel={getPriceLabel(intl, TRADE_DIRECTION_SELL)}
                priceHelpText={getPriceHelpText(intl, intlCurrency, TRADE_DIRECTION_SELL)}
                priceValidation={isCommunityPriceValid(intlCurrency, TRADE_DIRECTION_SELL)}
              />
            )}
            {!isEditing() && (
              <Button color="darken" onClick={() => addClause(TRADE_DIRECTION_SELL)}>
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {addClauseLabel}
              </Button>
            )}
          </div>
        </CardBody>
        <CardFooter className="d-flex">
          <Button color="primary" className="me-2" disabled={processing || isEditing() || !areBuyAndSellClausesValid}>
            {setLabel}
          </Button>
          <Button color="" onClick={() => (router.go(-1))} disabled={processing || isEditing()}>
            {cancelLabel}
          </Button>
          <Button
            color="darken"
            className="ms-auto"
            onClick={() => {
              const newClauses = copyFromResidual();
              if (newClauses) {
                setBuyClauses(newClauses.buyClauses);
                setSellClauses(newClauses.sellClauses);
                setIgnorePublicHolidays(newClauses.ignorePublicHolidays);
              }
            }}
            disabled={processing || isEditing() || !copyFromResidual()}
          >
            <FontAwesomeIcon icon={faClone} className="me-2" />
            {copyFromResidualLabel}
          </Button>
        </CardFooter>
      </Card>
      <Modal
        isOpen={showConfirmCoverage}
        toggle={() => setShowConfirmCoverage(!showConfirmCoverage)}
      >
        <ModalHeader toggle={() => setShowConfirmCoverage(!showConfirmCoverage)}>
          {noFullCoverageLabel}
        </ModalHeader>
        <ModalBody>
          {noFullCoverageHelpText}
        </ModalBody>
        <ModalFooter>
          <Button color="primary" className="me-2" onClick={handleSubmit}>
            {confirmLabel}
          </Button>
          <Button color="" onClick={() => setShowConfirmCoverage(!showConfirmCoverage)}>
            {continueEditingLabel}
          </Button>
        </ModalFooter>
      </Modal>
    </Form>
  );
}

TimeOfUsePricingForm.propTypes = {
  property: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  meter: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  router: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  onPricingButtonClick: PropTypes.func.isRequired,
  handleTradeRuleProposeCommunity: PropTypes.func.isRequired,
  processing: PropTypes.bool.isRequired,
};

export default TimeOfUsePricingForm;
